import ApiDatabase from 'server';

// Calls of the prospection day. Each one returns a Promise; a rejection keeps
// the axios error, whose status and errorCode are read by helper/apiErrorText.js.
// ApiDatabase is read at call time, so that a test's mock of server/index.js is the one called.
const call = (method, body) =>
  new Promise((resolve, reject) => {
    ApiDatabase[method](body, resolve, reject);
  });

export const openDaySession = () => call('openCrmDaySession', {});

export const getCallDay = () => call('getCrmCallDay', {});

// The queue of the day (the 'new' lines of getCallDay) in its new order; the api
// keeps it and answers { ids } as applied.
export const setCallOrder = ({ ids }) => call('setCrmCallOrder', { ids });

// A run of calls (« Lancer »): opened with the number of calls planned,
// closed with the calls left aside. No pause is measured: 0.
export const startCallSession = ({ planned }) => call('startCrmCallSession', { planned });

export const endCallSession = ({ id, skipped }) =>
  call('endCrmCallSession', { id, pausedSeconds: 0, skipped });

// One's ended sessions, the latest first, each with its stats (as endCallSession answers).
export const listCallSessions = () => call('listCrmCallSessions', {});

// A session with its calls, the earliest first: { ...session, calls: [{ id,
// result, note, at, contactName, company: { id, name, city } | null }] }, a
// deleted company null. Also another's, for the agency manager.
export const getCallSession = ({ id }) => call('getCrmCallSession', { id });

export const getGlobalList = () => call('getCrmGlobalList', {});

// A Commercial's (510) prospection, read-only, for the agency manager (500):
// { owner, day | null, globalList, prospects, sessions }; day is null while he
// has not opened his day. The api writes nothing for this read.
export const getSalesRepOverview = ({ ownerId }) =>
  call('getCrmCallOverview', { ownerId });

export const addToCallDay = ({ companyId, sessionId }) =>
  call('addCrmCallToDay', sessionId ? { companyId, sessionId } : { companyId });

export const listProspects = () => call('getCrmProspects', {});

// A prospect put aside (« Pas intéressé », lost) back to prospection, so that it may be called again.
export const reactivateProspect = ({ companyId }) => call('reactivateCrmProspect', { companyId });

export const removeFromCallDay = ({ callbackId }) => call('removeCrmCallFromDay', { callbackId });

// The undo object is sent back exactly as the api gave it.
export const restoreToCallDay = (undo) => call('restoreCrmCallToDay', undo);

// The people of the CRM ({ id, firstname, lastname, role }): the commercials
// (510) are the ones a prospect can be given to.
export const listTeam = () => call('getCrmTeam', {});

export const createProspect = ({ fields, addToday, sessionId }) =>
  call('createCrmProspect', sessionId ? { fields, addToday, sessionId } : { fields, addToday });

export const checkSirets = async (sirets) => {
  if (!sirets?.length) return [];
  const { followed } = await call('checkCrmProspectSirets', { sirets });
  return followed ?? [];
};

// Only the fields of the contract are sent; an empty callbackId or sessionId is left out
// (a call opened off the list closes no callback). phoneCallId: the Wildix call qualified.
export const confirmCall = ({
  companyId,
  callbackId,
  sessionId,
  result,
  note,
  contactName,
  followUp,
  phoneCallId,
}) =>
  call('confirmCrmCall', {
    companyId,
    ...(callbackId ? { callbackId } : {}),
    ...(sessionId ? { sessionId } : {}),
    result,
    note: note ?? '',
    ...(contactName ? { contactName } : {}),
    ...(followUp ? { followUp } : {}),
    ...(phoneCallId ? { phoneCallId } : {}),
  });

// « Nouveau contact » of « Rappeler qui ? »: added to the company's contacts
// (JBWEB-1424), answered { contact } with its id.
export const addCompanyContact = ({ companyId, contact }) => call('saveCrmCompanyContact', { companyId, contact });

// Calls of the company, most recent first.
export const getCallHistory = async (companyId) => {
  const { calls } = await call('getCrmCallHistory', { companyId });
  return calls ?? [];
};

// The AI summary of the prospect's latest call notes: { points: string[] | null, at }.
// null points: no call note, the api asked nothing of the AI.
export const getProspectSummary = ({ companyId }) =>
  call('getCrmProspectSummary', { companyId });

// One's Wildix call still live (< 2 h), else the one completed in the last
// 10 minutes, else null: { call: LiveCall | null }. Read when the page opens;
// the socket event 'crmPhoneCall' brings the next updates.
// phoneCallId: one's call of that Wildix id instead, whatever its age, or null
// (a call to qualify from Home).
export const getLiveCall = async ({ phoneCallId } = {}) => {
  const { call: liveCall } = await call(
    'getCrmPhoneLive',
    phoneCallId ? { phoneCallId } : {},
  );
  return liveCall ?? null;
};

// The socket of the admin (its room is one's user id), null when it cannot connect.
export const connectSocket = () => ApiDatabase.connectSocketWithToken();
